/**
 * THE SCREEN CONTRACT AND THE ROUTER. One screen is on the page at a time, structurally.
 *
 * ⭐ **THIS FILE IS THE ANSWER TO THE TWO DEFECTS THAT COST THE MOST IN THE OLD CLIENT**, and both
 * are now impossible rather than checked:
 *
 * 1. **TWO SCREENS LIVE AT ONCE.** The old architecture drew our page over theirs and arbitrated
 *    with `Precedence`, `ChromeKeys`, `When` and `RevivalPageLife`. Measured 2026-08-13:
 *    `RevivalMenu` and `RevivalCampaignScreen` retired each other **222 times in one navigation**,
 *    and `RevivalCampaignScreen` tore down **171 times in one three-minute walk** - that churn IS
 *    the flashing Alex reported for days. Here `show()` REPLACES the mount's contents. There is no
 *    second screen to arbitrate with, so there is nothing to tune.
 *
 * 2. **A SCREEN A PLAYER CANNOT LEAVE.** Measured 2026-08-14: THE LEAGUES had no PLAY and no BACK,
 *    and every gate we owned scored it green because they all measured appearance. Here a screen
 *    DECLARES what a player can do on it and the router refuses to show one that cannot be left.
 *
 * ⚠️ **THE CHECK RUNS AFTER MOUNT, AGAINST THE REAL DOM.** Asserting against the screen's own
 * declaration would be a mirror - it would pass for a screen that declared a BACK button and forgot
 * to render it, which is precisely what happened. It asks the document.
 */

import { setScreen } from "./diagnostics";
export interface Screen {
  /** Stable id, used for the route and for evidence filenames. */
  readonly id: string;

  /**
   * What a player must be able to DO here. Checked against the rendered DOM after mount.
   *
   * ⭐ **AN ENTRY MAY BE A SET OF ALTERNATIVES, and one screen genuinely needs that.** The deck
   * builder declared `SAVE`, and a STARTER deck is read-only by design (Alex, 2026-08-14: *"the
   * edit or delete function should not even show for the free pre-built decks"*) so it renders
   * COPY TO A NEW DECK instead. Four of a player's five decks are starters, so the DEFAULT view of
   * that screen logged MISSING AFFORDANCE on every single visit, live.
   *
   * ⛔ **THAT IS THE FAILURE MODE THIS CHECK EXISTS TO AVOID, TURNED ON ITSELF:** an alarm that
   * cries wolf on a correct screen gets read as noise, and then the real one is noise too. The
   * requirement was never "SAVE"; it was "a way to commit your work", which is one of two controls
   * depending on whose deck it is. Say that, rather than weakening the declaration to `[BACK]`.
   */
  readonly mustOffer?: readonly (string | readonly string[])[];

  /**
   * The one screen a player RETURNS to needs no way out of itself.
   * ⛔ Set this on the menu and nowhere else. It is not a way to quieten the check.
   */
  readonly isBase?: boolean;

  /** Build the screen's DOM. Called once per visit; the screen is discarded on leave. */
  mount(host: HTMLElement, nav: Navigator): void | Promise<void>;

  /** Release timers, listeners, tickers. Called before the host is emptied. */
  unmount?(): void;
}

export interface Navigator {
  go(id: string): void;
  back(): void;
}

import { releasePageChrome } from "../ui/page";

/** Labels that mean "a player can get out of here". Any one satisfies the way-out rule. */
const WAY_OUT = ["BACK", "CLOSE", "CANCEL", "RETURN", "LEAVE", "DONE"];

export class Router implements Navigator {
  private readonly screens = new Map<string, Screen>();
  private readonly stack: string[] = [];
  private current: Screen | null = null;

  constructor(private readonly host: HTMLElement) {}

  register(s: Screen): void {
    if (this.screens.has(s.id)) {
      throw new Error(
        `[router] duplicate screen id '${s.id}'. Two owners of one screen is the defect this ` +
          `whole rebuild exists to remove.`,
      );
    }
    this.screens.set(s.id, s);
  }

  go(id: string): void {
    void this.show(id, true);
  }

  /**
   * ⭐ IN-APP BACK AND BROWSER BACK ARE THE SAME GESTURE and must not be two mechanisms. Delegating
   * to `history.back()` means the internal stack is maintained in ONE place - the `hashchange`
   * handler - rather than here as well, which is how the two drift apart and the player gets a
   * different destination depending on which control they used.
   */
  back(): void {
    // ⚠️ `history.back()` only helps if there IS an entry beneath us. On a deep link the player
    // arrived directly, so the app is the first entry and going back would leave the site - the very
    // bug this change exists to fix, mirrored. In that case fall back to the internal stack.
    if (this.stack.length > 1) {
      history.back();
      return;
    }
    void this.show("menu", true);
  }

  /** Every registered screen id, in registration order. Read by the screen walker. */
  ids(): string[] {
    return Array.from(this.screens.keys());
  }

  /**
   * Start listening to the address bar, and honour whatever is already there.
   *
   * ⭐ **EVERY SCREEN IS ADDRESSABLE, AND THAT IS WORTH MORE THAN NAVIGATION.** It makes the invite
   * link (`#/duels?room=CODE`) a route rather than a special case, it lets the browser's own back
   * button work, and it turns "walk every surface" - which took a bespoke Unity harness, twelve
   * minutes a run, and still measured the wrong screen when a match was left live - into a list of
   * URLs.
   *
   * ⚠️ An unknown hash falls back to the menu rather than throwing. A stale bookmark is a player
   * with a broken game otherwise, and the address bar is not a trusted input.
   */
  start(fallback = "menu"): void {
    const fromHash = () => {
      const id = location.hash.replace(/^#\/?/, "").split("?")[0] ?? "";
      if (!id) return fallback;
      if (!this.screens.has(id)) {
        console.warn(`[router] unknown route '${id}' - falling back to '${fallback}'`);
        return fallback;
      }
      return id;
    };

    // 🔴 **HISTORY-DRIVEN NAVIGATION MUST NOT PUSH.** `hashchange` fires for a BACK/FORWARD press as
    // well as for a hand-edited address bar, and the entry already exists in both cases. Pushing here
    // would add an entry every time the player pressed Back, so Back would never actually go back.
    window.addEventListener("hashchange", () => {
      const id = fromHash();
      if (id === this.stack[this.stack.length - 1]) return;

      // Keep the internal stack honest against the browser's. If the target is the one BENEATH the
      // top, this was a real Back and the top is dropped; anything else is a jump, so the top is
      // replaced rather than accumulated.
      if (this.stack.length > 1 && id === this.stack[this.stack.length - 2]) this.stack.pop();
      else this.stack[this.stack.length - 1] = id;

      void this.show(id, false, true);
    });

    void this.show(fromHash());
  }

  /**
   * @param fromAddressBar the id came from the URL, which is UNTRUSTED INPUT rather than a call.
   */
  async show(id: string, push = true, fromAddressBar = false): Promise<void> {
    const next = this.screens.get(id);
    if (!next) {
      // 🚨 **A BAD URL AND A BAD `go()` ARE DIFFERENT FAULTS AND MUST BE TREATED DIFFERENTLY.**
      // A programmatic `go("typo")` is a bug in our code and must be loud. A hash typed, bookmarked
      // or shared by a player is INPUT - and throwing on it leaves a blank page, which is the one
      // outcome worse than a wrong screen. Found when `#/settings` stopped being a screen: the
      // controls moved into an overlay (correctly), and an old bookmark would have blanked.
      if (fromAddressBar) {
        console.warn(`[router] no screen '${id}' - falling back to the menu`);
        await this.show("menu", true);
        return;
      }
      throw new Error(`[router] no screen registered as '${id}'`);
    }

    this.current?.unmount?.();
    // ⚠️ The page shell owns a Pixi backdrop that is NOT a child of the screen's own teardown path.
    // One per visited screen is a leak whose symptom is the lobby getting slower the longer it is
    // used - the same class of bug the menu's `unmount` already guards against.
    releasePageChrome();
    this.host.replaceChildren();

    this.current = next;
    if (push) this.stack.push(id);
    document.documentElement.dataset["screen"] = id;
    // ⭐ The bug reporter must know WHERE the player was without parsing the address bar, which
    // carries query strings and can lag a redirect. The router already owns this fact; it just had
    // no one to tell. See `app/diagnostics.ts`.
    setScreen(id);

    // 🔴 **PUSH ON THE WAY FORWARD, REPLACE OTHERWISE. THIS WAS `replaceState` UNCONDITIONALLY.**
    //
    // Alex, 2026-08-17: *"when i press the back arrow in brave i do not go to the previous page in
    // the game, i get sent to the brave initial select a page screen."* With `replaceState` on every
    // navigation the browser's history NEVER GROWS: the whole app is one entry, so the first Back
    // press leaves the site. The comment three lines above this one claimed the design "lets the
    // browser's own back button work", which is the property the code was preventing - a stated
    // intention that the implementation had quietly inverted.
    //
    // ⚠️ THE ORIGINAL WORRY WAS REAL BUT AIMED AT THE WRONG API. Assigning `location.hash` fires
    // `hashchange` and re-enters `show`. `history.pushState` does NOT fire it, so pushing is safe
    // here and is exactly the call that gives the player a back button.
    //
    // `push` is false for the initial load and for a history-driven navigation - neither should add
    // an entry, or Back would need pressing twice and then never arrive.
    if (location.hash.replace(/^#\/?/, "").split("?")[0] !== id) {
      if (push && !fromAddressBar) history.pushState(null, "", `#/${id}`);
      else history.replaceState(null, "", `#/${id}`);
    }

    await next.mount(this.host, this);
    this.assertAffordances(next);
  }

  /**
   * CAN A PLAYER ACTUALLY USE WHAT WE JUST RENDERED? Asked of the DOM, not of the declaration.
   *
   * ⚠️ **IT REPORTS, IT DOES NOT REPAIR.** The old framework drew a fallback BACK because it was
   * patching a shipped client we could not change. Here the screen is ours, so a missing exit is a
   * bug to fix at the source; auto-inserting one would hide it. The console error is deliberately
   * unmissable and `verify_affordances` reads the same signal in CI.
   */
  private assertAffordances(s: Screen): void {
    const controls = Array.from(
      this.host.querySelectorAll<HTMLElement>("button, [role='button'], a[href]"),
    ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-disabled") !== "true");

    const labels = controls
      .map((el) => (el.dataset["label"] ?? el.textContent ?? "").trim().toUpperCase())
      .filter(Boolean);

    const has = (want: string) => labels.includes(want.trim().toUpperCase());
    // An entry that is an array is satisfied by ANY of its members. See `mustOffer` above.
    const met = (m: string | readonly string[]) => Array.isArray(m) ? m.some(has) : has(m as string);
    const name = (m: string | readonly string[]) =>
      Array.isArray(m) ? `${m.join(" or ")}` : String(m);
    const missing = (s.mustOffer ?? []).filter((m) => !met(m));
    const needsExit = !s.isBase;
    const hasExit = WAY_OUT.some(has);

    const shown = labels.length ? labels.join(", ") : "(none)";

    if (needsExit && !hasExit) {
      console.error(
        `[${s.id}] NO WAY OUT: this screen rendered no control a player can leave by. ` +
          `Its controls are: ${shown}. A screen with no exit is a DEAD END - set isBase if it is ` +
          `the menu, otherwise render one of ${WAY_OUT.join("/")}.`,
      );
    }

    if (missing.length) {
      console.error(
        `[${s.id}] MISSING AFFORDANCE: declared mustOffer [${(s.mustOffer ?? []).map(name).join(", ")}] ` +
          `but rendered [${missing.map(name).join(", ")}] nowhere. Its controls are: ${shown}.`,
      );
    }

    // ⭐ SAY SO WHEN IT PASSES. An instrument only ever seen returning silence cannot be proven to
    // have run - `harness-engineering`'s positive control, applied to our own runtime.
    if (!missing.length && (!needsExit || hasExit)) {
      console.info(`[${s.id}] affordances OK: ${shown}${needsExit ? " (has a way out)" : " (base screen)"}`);
    }
  }
}
